import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Download } from "lucide-react";
import { getJobProfitability } from "@/lib/accounting/accounting.functions";
import { downloadCSV, fmtMoney, toCSV } from "@/lib/accounting/exports";

export const Route = createFileRoute("/_authenticated/admin/accounting/profitability")({
  component: Profit,
});

function Profit() {
  const fn = useServerFn(getJobProfitability);
  const { data, isLoading } = useQuery({ queryKey: ["accounting", "profitability"], queryFn: () => fn() });
  if (isLoading || !data) return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;

  const exportCsv = () => {
    downloadCSV(`profitability_${new Date().toISOString().slice(0, 10)}.csv`, toCSV(data.map((r: any) => ({
      project: r.project_name, customer: r.customer_name, proposal_total: r.proposal_total,
      invoice_total: r.invoice_total, payments_collected: r.payments_collected,
      materials: r.materials_cost, labor: r.labor_cost, other: r.other_costs, total_costs: r.total_costs,
      gross_profit: r.gross_profit, margin_pct: r.profit_margin.toFixed(1), balance_due: r.balance_due,
    }))));
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs text-zinc-200 hover:bg-white/5"><Download className="size-3.5" /> CSV</button>
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
            <tr>
              <th className="text-left px-4 py-3">Job</th><th className="text-left px-4 py-3">Customer</th>
              <th className="text-right px-4 py-3">Invoiced</th><th className="text-right px-4 py-3">Collected</th>
              <th className="text-right px-4 py-3">Materials</th><th className="text-right px-4 py-3">Labor</th>
              <th className="text-right px-4 py-3">Other</th><th className="text-right px-4 py-3">Profit</th>
              <th className="text-right px-4 py-3">Margin</th><th className="text-right px-4 py-3">Balance</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr><td colSpan={10} className="text-center py-12 text-zinc-500">No projects yet.</td></tr>
            ) : data.map((r: any) => (
              <tr key={r.project_id} className="border-b border-white/5">
                <td className="px-4 py-3 text-white">{r.project_name}</td>
                <td className="px-4 py-3 text-zinc-300">{r.customer_name}</td>
                <td className="px-4 py-3 text-right">{fmtMoney(r.invoice_total)}</td>
                <td className="px-4 py-3 text-right text-emerald-300">{fmtMoney(r.payments_collected)}</td>
                <td className="px-4 py-3 text-right text-zinc-300">{fmtMoney(r.materials_cost)}</td>
                <td className="px-4 py-3 text-right text-zinc-300">{fmtMoney(r.labor_cost)}</td>
                <td className="px-4 py-3 text-right text-zinc-300">{fmtMoney(r.other_costs)}</td>
                <td className={`px-4 py-3 text-right ${r.gross_profit >= 0 ? "text-emerald-300" : "text-red-300"}`}>{fmtMoney(r.gross_profit)}</td>
                <td className={`px-4 py-3 text-right ${r.profit_margin >= 30 ? "text-emerald-300" : r.profit_margin >= 0 ? "text-amber-300" : "text-red-300"}`}>{r.profit_margin.toFixed(1)}%</td>
                <td className="px-4 py-3 text-right text-amber-300">{fmtMoney(r.balance_due)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}